import { useEffect, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Copy,
  Eye,
  KeyRound,
  Loader2,
  MoreHorizontal,
  RefreshCw,
  Shield,
  Trash2,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";

import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useConfirm } from "@/components/ConfirmDialog";
import { InviteLinksSection } from "@/components/InviteLinksSection";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ROLE_OPTIONS = ["owner", "editor", "viewer"];

function membersReducer(state, action) {
  switch (action.type) {
    case "loaded":
      return { members: action.members, loading: false };
    case "set":
      return { ...state, members: action.members };
    case "remove":
      return { ...state, members: state.members.filter((m) => m.user_id !== action.userId) };
    default:
      return state;
  }
}

export default function TeamMembersPage() {
  const { user, emulateAs } = useAuth();
  const { current } = useWorkspace();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [{ members, loading }, dispatchMembers] = useReducer(membersReducer, {
    members: [],
    loading: true,
  });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("editor");
  const [inviting, setInviting] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState("");
  const [busyMemberId, setBusyMemberId] = useState(null);
  // {kind:"invite"|"password", email, link?, password?}
  const [showCredentials, setShowCredentials] = useState(null);

  const myRole = current?.my_role;
  const canManage = myRole === "owner";
  const isPersonal = current?.is_personal === true;
  const canInvite = canManage && !isPersonal;

  useEffect(() => {
    if (!current) return;
    api
      .get(`/workspaces/${current.id}/members`)
      .then(({ data }) => dispatchMembers({ type: "loaded", members: data }))
      .catch((e) => {
        toast.error(formatApiError(e));
        dispatchMembers({ type: "loaded", members: [] });
      });
  }, [current]);

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const { data } = await api.post(`/workspaces/${current.id}/invite`, {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      const full = data.invite_url_absolute || `${window.location.origin}${data.invite_url}`;
      setLastInviteLink(full);
      navigator.clipboard?.writeText(full).catch(() => {});
      const { data: m } = await api.get(`/workspaces/${current.id}/members`);
      dispatchMembers({ type: "set", members: m });
      setInviteEmail("");
      if (data.email_sent) {
        toast.success(`Invite email sent to ${data.email} — link also copied to clipboard`);
      } else if (data.email_configured === false) {
        toast.success(
          `Invite link copied for ${data.email}. Set up email in Platform Settings to auto-send invites.`,
        );
      } else {
        toast.success(`Invite link copied for ${data.email} (email dispatch failed — share the link manually)`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (memberUserId, role) => {
    try {
      await api.put(`/workspaces/${current.id}/members/${memberUserId}/role`, { role });
      const { data } = await api.get(`/workspaces/${current.id}/members`);
      dispatchMembers({ type: "set", members: data });
      toast.success("Role updated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleRemove = async (member) => {
    if (member.is_primary_owner) {
      toast.error("Cannot remove the primary owner");
      return;
    }
    const label = member.email || "this member";
    const ok = await confirm({
      title: "Remove this member?",
      description: `${label} will lose access to this workspace. You can re-invite them anytime.`,
      confirmLabel: "Remove",
      danger: true,
      testId: "confirm-remove-member",
    });
    if (!ok) return;
    try {
      await api.delete(`/workspaces/${current.id}/members/${member.user_id}`);
      dispatchMembers({ type: "remove", userId: member.user_id });
      toast.success("Member removed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleResend = async (member) => {
    setBusyMemberId(member.user_id);
    try {
      const { data } = await api.post(
        `/workspaces/${current.id}/members/${member.user_id}/resend-invite`,
      );
      const fullLink = data.invite_url_absolute || `${window.location.origin}${data.invite_url}`;
      navigator.clipboard?.writeText(fullLink).catch(() => {});
      setShowCredentials({ kind: "invite", email: data.email, link: fullLink });
      if (data.email_sent) {
        toast.success(`Invite email re-sent to ${data.email} — link also copied`);
      } else if (data.email_configured === false) {
        toast.success(`New invite link copied — share it with ${data.email}`);
      } else {
        toast.success(`Invite link copied for ${data.email} (email dispatch failed)`);
      }
      const { data: m } = await api.get(`/workspaces/${current.id}/members`);
      dispatchMembers({ type: "set", members: m });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyMemberId(null);
    }
  };

  const handleResetPassword = async (member) => {
    const ok = await confirm({
      title: "Reset this member's password?",
      description: `A new temporary password will be generated for ${member.email}. The current password will stop working immediately. Share the new password with them securely.`,
      confirmLabel: "Reset password",
      danger: true,
      testId: "confirm-reset-password",
    });
    if (!ok) return;
    setBusyMemberId(member.user_id);
    try {
      const { data } = await api.post(
        `/workspaces/${current.id}/members/${member.user_id}/reset-password`,
      );
      navigator.clipboard?.writeText(data.temporary_password).catch(() => {});
      setShowCredentials({
        kind: "password",
        email: data.email,
        password: data.temporary_password,
      });
      toast.success(`Temporary password copied for ${data.email}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyMemberId(null);
    }
  };

  const handleEmulate = async (member) => {
    const ok = await confirm({
      title: `Emulate ${member.email}?`,
      description: `You'll see Probe exactly as ${member.email} does — including their projects, tests, and runs. Use the "Return to my account" banner at the top to come back.`,
      confirmLabel: "Emulate",
      testId: "confirm-emulate-member",
    });
    if (!ok) return;
    setBusyMemberId(member.user_id);
    try {
      await emulateAs(current.id, member.user_id);
      toast.success(`Now viewing as ${member.email}`);
      navigate("/projects");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyMemberId(null);
    }
  };

  if (!current) return null;

  return (
    <div
      className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10"
      data-testid="team-members-page"
    >
      <div className="mb-8">
        <h1 className="font-['Geist'] font-semibold text-2xl sm:text-[28px] tracking-tight text-[#0F172A]">
          Team Members
        </h1>
        <p className="text-sm text-[#64748B] mt-1.5">
          Manage who has access to <span className="font-medium text-[#0F172A]">{current.name}</span>.
          {isPersonal
            ? " This is a personal workspace — teammates can't be invited here. Need to collaborate? Contact your account manager."
            : canManage
              ? " You can invite teammates, change roles, and remove access here."
              : " Only workspace owners can add or remove members."}
        </p>
      </div>

      <div className="max-w-3xl">
        {canInvite && <InviteLinksSection workspace={current} />}
      </div>

      <div className="surface-card max-w-3xl">
        <div className="px-5 py-4 border-b border-[#F1F5F9] flex items-center justify-between">
          <div>
            <h2 className="font-['Geist'] font-semibold text-lg tracking-tight text-[#0F172A]">
              Members
            </h2>
            <p className="text-sm text-[#64748B]">
              {members.length} member{members.length === 1 ? "" : "s"}
            </p>
          </div>
          {canInvite && (
            <button
              onClick={() => {
                // Reset the dialog every time it opens — the previous
                // invite's email/role/link shouldn't bleed into the next
                // invite attempt.
                setInviteEmail("");
                setInviteRole("editor");
                setLastInviteLink("");
                setInviteOpen(true);
              }}
              data-testid="invite-member-button"
              className="flex items-center gap-1.5 h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB]"
            >
              <UserPlus size={13} /> Invite
            </button>
          )}
        </div>

        {loading ? (
          <div className="p-5 text-sm text-[#64748B]">Loading…</div>
        ) : (
          <ul data-testid="members-list" className="divide-y divide-[#F1F5F9]">
            {members.map((m) => {
              const isMe = m.user_id === user.id;
              const isPrimary = m.is_primary_owner;
              const isSuper = m.global_role === "super_admin";
              const displayName = m.name || (m.email ? m.email.split("@")[0] : "Pending teammate");
              const displayEmail = m.email || "Unknown email";
              const canModifyRole = canManage && !isMe && !m.pending && !isPrimary;
              const canResend = canInvite && m.pending;
              const canEmulate = canManage && !isMe && !m.pending && !isSuper;
              const canReset = canManage && !m.pending && !isSuper && (!isPrimary || isMe);
              const canRemoveRow = canManage && !isMe && !isPrimary;
              return (
                <li
                  key={m.user_id}
                  data-testid={`member-row-${m.user_id}`}
                  className="px-5 py-3.5 flex items-center gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0F172A] truncate flex items-center gap-2">
                      <span data-testid={`member-name-${m.user_id}`}>{displayName}</span>
                      {isPrimary && (
                        <span
                          data-testid={`member-primary-badge-${m.user_id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border border-[#3B82F6]/25 bg-[#3B82F6]/10 text-[#1D4ED8]"
                          title="Primary owner — cannot be removed"
                        >
                          <Shield size={10} /> Primary
                        </span>
                      )}
                      {isMe && (
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#475569]">
                          You
                        </span>
                      )}
                    </p>
                    <p
                      className="text-xs text-[#64748B] truncate flex items-center gap-2"
                      data-testid={`member-email-${m.user_id}`}
                    >
                      {displayEmail}
                      {m.pending && (
                        <span
                          data-testid={`member-pending-${m.user_id}`}
                          className="text-xs font-medium px-2 py-0.5 rounded-full border border-[#F59E0B]/20 bg-[#F59E0B]/10 text-[#B45309]"
                        >
                          Invite pending
                        </span>
                      )}
                    </p>
                  </div>
                  {canModifyRole ? (
                    <Select
                      value={m.role}
                      onValueChange={(v) => handleRoleChange(m.user_id, v)}
                    >
                      <SelectTrigger
                        data-testid={`member-role-${m.user_id}`}
                        className="h-8 w-[110px] rounded-md border-[#E5E7EB] bg-white text-sm capitalize text-[#334155] px-2.5 focus:ring-[#3B82F6]"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-md border-[#E5E7EB]">
                        {ROLE_OPTIONS.map((r) => (
                          <SelectItem key={r} value={r} className="capitalize text-sm">
                            {r}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span
                      className="text-xs text-[#64748B] capitalize"
                      data-testid={`member-role-static-${m.user_id}`}
                    >
                      {m.role}
                    </span>
                  )}
                  {canManage && (canResend || canEmulate || canReset || canRemoveRow) ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid={`member-actions-${m.user_id}`}
                          disabled={busyMemberId === m.user_id}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] text-[#64748B] hover:text-[#0F172A] hover:bg-[#F8FAFC] disabled:opacity-50"
                          aria-label="Member actions"
                          title="Member actions"
                        >
                          {busyMemberId === m.user_id ? (
                            <Loader2 size={13} className="animate-spin" />
                          ) : (
                            <MoreHorizontal size={14} />
                          )}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        {canResend && (
                          <DropdownMenuItem
                            onClick={() => handleResend(m)}
                            data-testid={`member-resend-${m.user_id}`}
                          >
                            <RefreshCw size={12} className="mr-2 text-[#3B82F6]" />
                            Resend invite
                          </DropdownMenuItem>
                        )}
                        {canEmulate && (
                          <DropdownMenuItem
                            onClick={() => handleEmulate(m)}
                            data-testid={`member-emulate-${m.user_id}`}
                          >
                            <Eye size={12} className="mr-2 text-[#0EA5E9]" />
                            Emulate
                          </DropdownMenuItem>
                        )}
                        {canReset && (
                          <DropdownMenuItem
                            onClick={() => handleResetPassword(m)}
                            data-testid={`member-reset-pw-${m.user_id}`}
                          >
                            <KeyRound size={12} className="mr-2 text-[#F59E0B]" />
                            Reset password
                          </DropdownMenuItem>
                        )}
                        {canRemoveRow && (
                          <>
                            {(canResend || canEmulate || canReset) && <DropdownMenuSeparator />}
                            <DropdownMenuItem
                              onClick={() => handleRemove(m)}
                              data-testid={`member-remove-${m.user_id}`}
                              className="text-[#B91C1C] focus:bg-[#FEF2F2]"
                            >
                              <Trash2 size={12} className="mr-2" />
                              Remove from workspace
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Invite dialog */}
      <Dialog
        open={inviteOpen}
        onOpenChange={(v) => {
          setInviteOpen(v);
          if (!v) setLastInviteLink("");
        }}
      >
        <DialogContent className="rounded-xl border-[#E5E7EB]" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Geist'] font-semibold tracking-tight">
              Invite a teammate
            </DialogTitle>
            <DialogDescription>
              We&apos;ll email them a one-click invite. The link is also copied to your clipboard as
              a backup, and you can re-send the invite as many times as needed from the member row.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-[#475569] block mb-1.5">Email</label>
              <Input
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="teammate@company.com"
                type="email"
                className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6]"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#475569] block mb-1.5">Role</label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger
                  data-testid="invite-role-select"
                  className="w-full rounded-md border-[#E5E7EB] bg-white h-10 px-3 text-sm text-[#334155] focus:ring-[#3B82F6]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-md border-[#E5E7EB]">
                  <SelectItem value="editor" className="text-sm">Editor — can create, edit, and run tests</SelectItem>
                  <SelectItem value="viewer" className="text-sm">Viewer — read-only access</SelectItem>
                  <SelectItem value="owner" className="text-sm">Owner — full control, can invite</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {lastInviteLink && (
              <div
                className="rounded-md p-3 border border-[#3B82F6]/20 bg-[#3B82F6]/5 space-y-1.5"
                data-testid="invite-link-result"
              >
                <p className="text-xs text-[#475569]">
                  Invite link (also sent by email, copied to your clipboard)
                </p>
                <div className="flex items-center gap-2">
                  <p className="font-mono text-xs break-all text-[#0F172A] flex-1">{lastInviteLink}</p>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard
                        ?.writeText(lastInviteLink)
                        .then(() => toast.success("Invite link copied"))
                        .catch(() => toast.error("Could not copy — please copy manually"));
                    }}
                    data-testid="invite-link-copy"
                    title="Copy invite link"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] bg-white text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC] shrink-0"
                  >
                    <Copy size={13} />
                  </button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button
              onClick={() => setInviteOpen(false)}
              className="h-9 px-4 text-sm font-medium rounded-md border border-[#E5E7EB] bg-white hover:bg-[#F8FAFC]"
            >
              Close
            </button>
            <button
              onClick={handleInvite}
              disabled={inviting || !inviteEmail.trim()}
              data-testid="invite-submit"
              className="h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60"
            >
              {inviting ? "Sending…" : "Send invite email"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials reveal — for resend-invite link + reset-password temp pw */}
      <Dialog open={!!showCredentials} onOpenChange={(v) => !v && setShowCredentials(null)}>
        <DialogContent className="rounded-xl border-[#E5E7EB]" data-testid="credentials-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Geist'] font-semibold tracking-tight">
              {showCredentials?.kind === "invite" ? "New invite link" : "Temporary password"}
            </DialogTitle>
            <DialogDescription>
              {showCredentials?.kind === "invite"
                ? `Share this fresh invite link with ${showCredentials?.email}. The previous link has been revoked.`
                : `Share this password with ${showCredentials?.email} securely. Their previous password no longer works.`}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md p-3 border border-[#3B82F6]/20 bg-[#3B82F6]/5">
            <p className="text-[10px] uppercase tracking-wide text-[#475569] mb-1">
              {showCredentials?.kind === "invite" ? "Invite URL" : "Temporary password"}
            </p>
            <div className="flex items-center gap-2">
              <code
                className="font-mono text-xs break-all text-[#0F172A] flex-1"
                data-testid="credentials-dialog-value"
              >
                {showCredentials?.kind === "invite"
                  ? showCredentials?.link
                  : showCredentials?.password}
              </code>
              <button
                type="button"
                onClick={() => {
                  const v =
                    showCredentials?.kind === "invite"
                      ? showCredentials?.link
                      : showCredentials?.password;
                  navigator.clipboard
                    ?.writeText(v)
                    .then(() => toast.success("Copied"))
                    .catch(() => {});
                }}
                data-testid="credentials-dialog-copy"
                className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] bg-white text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC]"
                title="Copy to clipboard"
              >
                <Copy size={13} />
              </button>
            </div>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setShowCredentials(null)}
              data-testid="credentials-dialog-close"
              className="h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB]"
            >
              Done
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
